// Motor de Segmentación — Lógica pura para clasificación de cohorte
// Módulo sin dependencias de React, testeable con property-based testing

import { ETAPAS_COHORTE } from "./constants";

/**
 * Jerarquía de etapas del viaje (de menor a mayor avance).
 * Un paciente se asigna a la etapa más avanzada alcanzada.
 */
const JERARQUIA = ETAPAS_COHORTE.map((e) => e.key);

/**
 * Clasifica pacientes por etapa actual usando datos mock.
 * @param {object} data — cohorteData del mock
 * @param {object} filters — { periodo, centro, especialidad, prevision }
 * @param {function} applyFilters — función del FiltersContext
 * @returns {{ etapas: Array<{ etapa, pacientes, pctTotal, detenidos, pctDetenidos, alerta, color }>, kpis: object }}
 */
export function clasificarCohorte(data, filters, applyFilters) {
  if (!data || !data.etapas || data.etapas.length === 0) {
    return {
      etapas: [],
      kpis: { totalPacientes: 0, activos: 0, enFuga: 0, etapaMayorAcumulacion: "—" },
    };
  }

  const etapas = data.etapas.map((e) => {
    const pacientes = applyFilters(e.pacientes);
    const detenidos = applyFilters(e.detenidos_30d);
    const pctDetenidos = pacientes > 0 ? (detenidos / pacientes) * 100 : 0;
    return {
      etapa: e.etapa,
      pacientes,
      pctTotal: 0, // se calcula después
      detenidos,
      pctDetenidos: Math.round(pctDetenidos * 10) / 10,
      alerta: pctDetenidos > 50,
      color: e.color,
    };
  });

  // Ordenar de mayor a menor volumen
  etapas.sort((a, b) => b.pacientes - a.pacientes);

  // Calcular total y porcentajes
  const totalPacientes = etapas.reduce((sum, e) => sum + e.pacientes, 0);
  for (const e of etapas) {
    e.pctTotal = totalPacientes > 0 ? Math.round((e.pacientes / totalPacientes) * 1000) / 10 : 0;
  }

  // KPIs
  const etapasActivas = ["atendido", "con_indicacion", "lab_realizado", "imagen_realizada", "cirugia_realizada", "pago_registrado"];
  const etapasFuga = ["cancelado", "no_show", "lab_pendiente", "imagen_pendiente", "cirugia_pendiente"];

  const activos = etapas
    .filter((e) => etapasActivas.some((a) => e.etapa.toLowerCase().replace(/[-\s]/g, "_") === a))
    .reduce((sum, e) => sum + e.pacientes, 0);

  const enFuga = etapas
    .filter((e) => etapasFuga.some((f) => e.etapa.toLowerCase().replace(/[-\s]/g, "_") === f))
    .reduce((sum, e) => sum + e.pacientes, 0);

  const etapaMayorAcumulacion = etapas.length > 0 ? etapas[0].etapa : "—";

  return {
    etapas,
    kpis: { totalPacientes, activos, enFuga, etapaMayorAcumulacion },
  };
}

/**
 * Obtiene el desglose de una etapa específica.
 * @param {string} etapa — nombre de la etapa
 * @param {object} desgloseData — cohorteDesgloseData del mock
 * @param {object} filters — filtros globales
 * @param {function} applyFilters — función del FiltersContext
 * @returns {{ prevision, planIsapre, planUC, edad, centro } | null}
 */
export function obtenerDesglose(etapa, desgloseData, filters, applyFilters) {
  if (!desgloseData || !desgloseData[etapa]) {
    return null;
  }

  const raw = desgloseData[etapa];

  const applyToDesglose = (desglose) => {
    if (!desglose) return { labels: [], series: [], colors: [] };
    return {
      labels: desglose.labels,
      series: desglose.series.map((v) => applyFilters(v)),
      colors: desglose.colors || undefined,
    };
  };

  // Agrupar planes con < 10 pacientes en "Otros planes"
  const agruparPlanesChicos = (desglose) => {
    if (!desglose) return { labels: [], series: [] };
    const filtered = applyToDesglose(desglose);
    const labels = [];
    const series = [];
    let otrosTotal = 0;

    for (let i = 0; i < filtered.labels.length; i++) {
      if (filtered.series[i] < 10) {
        otrosTotal += filtered.series[i];
      } else {
        labels.push(filtered.labels[i]);
        series.push(filtered.series[i]);
      }
    }

    if (otrosTotal > 0) {
      labels.push("Otros planes");
      series.push(otrosTotal);
    }

    return { labels, series };
  };

  return {
    prevision: applyToDesglose(raw.prevision),
    planIsapre: agruparPlanesChicos(raw.planIsapre),
    planUC: agruparPlanesChicos(raw.planUC),
    edad: applyToDesglose(raw.edad),
    centro: applyToDesglose(raw.centro),
  };
}

/**
 * Determina la etapa más avanzada dado un set de presencias en fuentes.
 * Usado internamente y expuesto para testing.
 * @param {object} presencias — { busqueda: bool, agendamiento: bool, ... }
 * @returns {string|null} key de la etapa más avanzada
 */
export function etapaMasAvanzada(presencias) {
  let masAvanzada = null;
  for (const key of JERARQUIA) {
    if (presencias[key]) {
      masAvanzada = key;
    }
  }
  return masAvanzada;
}
